import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, message, Modal, Row, Select, Upload } from "antd";
import { useTranslation } from "react-i18next";
import { COMPLAINT_REASONS } from "../../../../../constants/DepartmentConstants";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import { useOrders } from "../../../../../data/hooks/firestore/useOrders";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { addDoc, collection } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { FIRESTORE_COMPLAINTS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import dayjs from "dayjs";
import { PlusOutlined } from "@ant-design/icons";
import { compressImage, getBase64 } from "../../../../../functions/images";
import { saveComplaintFilesToStorage } from "../../../../../functions/firebase/storage";

const { Option } = Select;
const { TextArea } = Input;

const GeneralComplaintModal = ({ visible, onClose, onSuccess, companyFilter }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { customers, isLoading: customersLoading } = useCustomers();
  const { data: orders, loading: ordersLoading } = useOrders(90); // Last 90 days
  const { products, isLoading: productsLoading } = useProducts();
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { email } = useAuth();
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // Watch form values
  const reasonValue = Form.useWatch("reason", form);
  const customerValue = Form.useWatch("customerId", form);
  const productValue = Form.useWatch("productId", form);

  // Filter orders when customer is selected
  useEffect(() => {
    if (customerValue && orders) {
      const customerOrders = orders
        .filter((order) => order.farmorderCustomerId === customerValue)
        .sort((a, b) => {
          // Sort by delivery date descending
          return dayjs(b.farmorderDeliveryDate).unix() - dayjs(a.farmorderDeliveryDate).unix();
        });
      setFilteredOrders(customerOrders);
      setSelectedCustomerId(customerValue);
    } else {
      setFilteredOrders([]);
      setSelectedCustomerId(null);
    }
  }, [customerValue, orders]);

  // Update selected order details
  useEffect(() => {
    const orderId = form.getFieldValue("orderId");
    if (orderId && orders) {
      const order = orders.find((o) => o.id === orderId);
      setSelectedOrder(order);
    } else {
      setSelectedOrder(null);
    }
  }, [form.getFieldValue("orderId"), orders]);

  // Update selected product details
  useEffect(() => {
    if (productValue && products) {
      const product = products.find((p) => p.id === productValue);
      setSelectedProduct(product);
    } else {
      setSelectedProduct(null);
    }
  }, [productValue, products]);

  const handleCancel = () => {
    form.resetFields();
    setSelectedCustomerId(null);
    setFilteredOrders([]);
    setSelectedOrder(null);
    setSelectedProduct(null);
    setFileList([]);
    setPreviewVisible(false);
    setPreviewImage("");
    setPreviewTitle("");
    onClose();
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf("/") + 1));
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = async (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error(t("notifications.file_type_error"));
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t("notifications.image_size_error"));
      return false;
    }

    try {
      const compressedFile = await compressImage(file, 0.8, 1024);
      return compressedFile;
    } catch (error) {
      console.error("Error compressing image:", error);
      message.error(t("notifications.error_processing_image"));
      return false;
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("upload")}</div>
    </div>
  );

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          try {
            const compressedFile = await compressImage(file, 0.8, 1024);
            imageFiles.push(compressedFile);
          } catch (error) {
            console.error("Error compressing pasted image:", error);
            message.error(t("notifications.error_processing_image"));
          }
        }
      }
    }

    if (imageFiles.length > 0) {
      const newFileList = imageFiles.map((file, index) => ({
        uid: `paste-${Date.now()}-${index}`,
        name: `pasted-image-${Date.now()}-${index}.png`,
        status: "done",
        originFileObj: file,
      }));

      setFileList([...fileList, ...newFileList]);
      message.success(t("notifications.image_added_successfully") || `${imageFiles.length} image(s) added`);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const customer = values.customerId ? customers.find((c) => c.id === values.customerId) : null;
      const order = values.orderId ? orders.find((o) => o.id === values.orderId) : null;

      let uploadedImageUrls = [];
      if (fileList.length > 0) {
        try {
          const fileArray = fileList.map((file) => file.originFileObj);
          const imageUrlArray = [];
          await saveComplaintFilesToStorage("complaints", fileArray, imageUrlArray);
          uploadedImageUrls = imageUrlArray;
        } catch (uploadError) {
          console.error("Error uploading files:", uploadError);
          message.error(t("notifications.error_uploading_files"));
        }
      }

      // Determine company code: use customer's company if available, otherwise use companyFilter from context
      const companyCode = customer?.company || companyFilter || null;

      // Build products array if product is selected
      const productsArray = [];
      if (selectedProduct) {
        productsArray.push({
          productId: selectedProduct.id,
          productName: selectedProduct.productName,
        });
      }

      const complaintData = {
        farmorderId: order?.farmorderId || null,
        farmorderCustomerId: customer?.id || null,
        company: customer?.customerName || null,
        companyCode: companyCode,
        invoiceId: order?.invoiceId || null,
        exactInvoiceId: null,
        reason: values.reason,
        customReason: values.reason === "other" ? values.customReason : null,
        complainingPerson: values.complainingPerson || null,
        contactDetails: values.contactDetails || null,
        description: values.description || null,
        products: productsArray,
        images: uploadedImageUrls,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      console.log("complaintData", complaintData);

      const complaintsRef = collection(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_COMPLAINTS_TABLE}`);
      await addDoc(complaintsRef, complaintData);

      message.success(t("notifications.complaint_created_successfully"));
      handleCancel();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error creating general complaint:", error);
      message.error(t("notifications.error_creating_complaint"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("new_complaint")}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {t("create_complaint")}
        </Button>,
      ]}
      width={700}
      destroyOnClose
    >
      <div onPaste={handlePaste} tabIndex={0} style={{ outline: "none" }}>
        <Form form={form} layout="vertical">
          {/*<div*/}
          {/*  style={{*/}
          {/*    backgroundColor: "#e6f7ff",*/}
          {/*    border: "1px solid #91d5ff",*/}
          {/*    borderRadius: "6px",*/}
          {/*    padding: "12px 16px",*/}
          {/*    marginBottom: "16px",*/}
          {/*  }}*/}
          {/*>*/}
          {/*  <p style={{ margin: 0, color: "#666" }}>{t("general_complaint_info")}</p>*/}
          {/*</div>*/}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customerId" label={t("customer.customer")} rules={[{ required: false }]}>
                <Select
                  showSearch
                  placeholder={t("search_and_select_customer")}
                  loading={customersLoading}
                  filterOption={(input, option) => {
                    return option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                  }}
                >
                  {customers?.map((customer) => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.customerName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="orderId" label={t("order.order")} rules={[{ required: false }]}>
                <Select
                  showSearch
                  placeholder={t("search_and_select_order")}
                  disabled={!selectedCustomerId}
                  loading={ordersLoading}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {filteredOrders.map((order) => (
                    <Option key={order.id} value={order.id}>
                      {order.farmorderId}{" "}
                      <span style={{ color: "#666", fontSize: 11 }}>
                        (Delivered: {dayjs(order.farmorderDeliveryDate).format("DD-MM-YYYY")})
                      </span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="productId" label={t("product")} rules={[{ required: false }]}>
                <Select
                  showSearch
                  placeholder={t("select_product_placeholder")}
                  loading={productsLoading}
                  allowClear
                  filterOption={(input, option) => {
                    return option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                  }}
                >
                  {products?.map((product) => (
                    <Option key={product.id} value={product.id}>
                      {product.productName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="reason"
                label={t("complaint_reason")}
                rules={[{ required: true, message: t("select_reason") }]}
              >
                <Select placeholder={t("select_reason")}>
                  {COMPLAINT_REASONS.map((reason) => (
                    <Option key={reason.id} value={reason.id}>
                      {t(reason.labelKey)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {reasonValue === "other" && (
                <Form.Item
                  name="customReason"
                  label={t("specify_custom_reason")}
                  rules={[{ required: true, message: t("enter_custom_reason") }]}
                >
                  <Input placeholder={t("enter_custom_reason")} />
                </Form.Item>
              )}
            </Col>
          </Row>

          {/*<Row gutter={16}>*/}
          {/*  <Col span={24}>*/}
          {/*    <Form.Item*/}
          {/*      name="department"*/}
          {/*      label={t("department_error")}*/}
          {/*      rules={[{ required: true, message: t("select_at_least_one_department") }]}*/}
          {/*    >*/}
          {/*      <Select placeholder={t("select_department")} allowClear>*/}
          {/*        {DEPARTMENTS.map((dept) => (*/}
          {/*          <Option key={dept.id} value={dept.id}>*/}
          {/*            {t(dept.nameKey)}*/}
          {/*          </Option>*/}
          {/*        ))}*/}
          {/*      </Select>*/}
          {/*    </Form.Item>*/}
          {/*  </Col>*/}
          {/*</Row>*/}

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="complainingPerson"
                label={t("complaining_person")}
                rules={[{ required: false, message: t("enter_complaining_person_name") }]}
              >
                <Input placeholder={t("enter_complaining_person_name")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="contactDetails" label={t("contact_details")} rules={[{ required: false }]}>
                <TextArea rows={3} placeholder={t("enter_contact_details")} maxLength={500} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={t("photos")}>
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                  beforeUpload={beforeUpload}
                >
                  {fileList.length >= 8 ? null : uploadButton}
                </Upload>
                <p style={{ marginTop: 8 }}>{t("upload_photos")}</p>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label={t("description")}
                rules={[{ required: true, message: t("form.enter_description") }]}
              >
                <TextArea rows={4} placeholder={t("describe_complaint_details")} maxLength={1000} showCount />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default GeneralComplaintModal;
